// apps/workflow/src/pages/designer/TaskDetail.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  ArrowLeft,
  User,
  Mail,
  Phone,
  MapPin,
  Briefcase,
  Calendar,
  FileText,
  Upload,
  Send,
  X,
  Download,
  Check,
  Clock,
  AlertCircle,
  CheckCircle,
  Eye,
  RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';
import pb from '../../lib/pocketbaseClient';

// ─── Helpers ──────────────────────────────────────────────────────
const getStatusBadge = (status) => {
  const config = {
    pending: { label: 'Pending', color: 'bg-amber-100 text-amber-800' },
    assigned: { label: 'Assigned', color: 'bg-blue-100 text-blue-800' },
    'in-progress': { label: 'In Progress', color: 'bg-indigo-100 text-indigo-800' },
    submitted: { label: 'Submitted', color: 'bg-purple-100 text-purple-800' },
    completed: { label: 'Completed', color: 'bg-green-100 text-green-800' },
  };
  return config[status] || { label: status || 'Unknown', color: 'bg-slate-100 text-slate-800' };
};

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return '—';
  }
};

// ─── Main Component ──────────────────────────────────────────────
const DesignerTaskDetail = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();

  const userRole = user?.role || 'designer';
  const isFreelancer = userRole === 'freelancer';
  const isAdmin = ['admin', 'super_admin', 'manager'].includes(userRole);

  const [task, setTask] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Submit modal state
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [submitFile, setSubmitFile] = useState(null);
  const [submitNote, setSubmitNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchTask = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const taskData = await pb.collection('project_tasks').getOne(id, {
        expand: 'project,project.client,assignedTo,completedBy,submittedBy',
        requestKey: null,
      });
      setTask(taskData);

      // Fetch submissions for this task
      const subs = await pb.collection('task_submissions').getFullList({
        filter: `task = "${id}"`,
        sort: '-created',
        requestKey: null,
      });
      setSubmissions(subs);
    } catch (error) {
      if (!error.isAbort) {
        console.error('Error fetching task:', error);
        toast.error('Task not found');
        navigate('/designer/tasks');
      }
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchTask();
  }, [fetchTask]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchTask();
    setRefreshing(false);
  };

  // ─── Actions ──────────────────────────────────────────────────
  const handleClaim = async () => {
    try {
      await pb.collection('project_tasks').update(id, {
        assignedTo: user.id,
        status: 'assigned',
      });
      toast.success('Task claimed successfully');
      fetchTask();
    } catch (error) {
      console.error('Claim error:', error);
      toast.error('Failed to claim task');
    }
  };

  const handleStart = async () => {
    try {
      await pb.collection('project_tasks').update(id, {
        status: 'in-progress',
      });
      toast.success('Work started');
      fetchTask();
    } catch (error) {
      console.error('Start error:', error);
      toast.error('Failed to start work');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!submitFile) {
      toast.error('Please select a file to submit');
      return;
    }
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('task', id);
      formData.append('designer', user.id);
      formData.append('file', submitFile);
      formData.append('version', '1');
      formData.append('status', 'pending-review');
      if (submitNote) formData.append('note', submitNote);

      await pb.collection('task_submissions').create(formData);

      await pb.collection('project_tasks').update(id, {
        status: 'submitted',
        submittedBy: user.id,
        submittedAt: new Date().toISOString(),
      });

      toast.success('Design submitted successfully!');
      setShowSubmitModal(false);
      setSubmitFile(null);
      setSubmitNote('');
      fetchTask();
    } catch (error) {
      console.error('Submit error:', error);
      toast.error('Failed to submit design');
    } finally {
      setSubmitting(false);
    }
  };

  const handleComplete = async () => {
    try {
      await pb.collection('project_tasks').update(id, {
        status: 'completed',
        completedBy: user.id,
        completedAt: new Date().toISOString(),
      });
      toast.success('Task completed!');
      fetchTask();
    } catch (error) {
      console.error('Complete error:', error);
      toast.error('Failed to complete task');
    }
  };

  const handleDownload = (submission) => {
    const url = pb.files.getUrl(submission, submission.file);
    window.open(url, '_blank');
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] space-y-4">
        <div className="w-12 h-12 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" />
        <p className="text-slate-500 font-medium">Loading task...</p>
      </div>
    );
  }

  if (!task) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-500">Task not found.</p>
        <button
          onClick={() => navigate('/designer/tasks')}
          className="mt-4 text-indigo-600 hover:underline"
        >
          ← Back to Tasks
        </button>
      </div>
    );
  }

  const status = getStatusBadge(task.status);
  const project = task.expand?.project;
  const client = project?.expand?.client;
  const assignedTo = task.expand?.assignedTo;

  const canClaim = !task.assignedTo && task.status !== 'completed' && task.status !== 'submitted';
  const isAssignedToMe = task.assignedTo === user?.id;
  const canStart = isAssignedToMe && task.status === 'assigned';
  const canSubmit = isAssignedToMe && task.status === 'in-progress';
  const canComplete = isAssignedToMe && task.status === 'submitted';

  return (
    <div className="space-y-6 max-w-4xl mx-auto p-4 md:p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/designer/tasks')}
            className="p-2 text-slate-500 hover:text-slate-700 transition rounded-lg hover:bg-slate-100"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-slate-800">{task.title}</h1>
            <div className="flex items-center gap-3 mt-1">
              <span className={`px-2 py-1 text-xs font-medium rounded-full ${status.color}`}>
                {status.label}
              </span>
              <span className="text-sm text-slate-500">
                Project: {project?.projectName || 'N/A'}
              </span>
              {task.category && (
                <span className="text-xs bg-slate-100 px-2 py-0.5 rounded-full">
                  {task.category}
                </span>
              )}
            </div>
          </div>
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="p-2 text-slate-400 hover:text-indigo-600 transition rounded-lg hover:bg-indigo-50 disabled:opacity-50"
        >
          <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Details */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 space-y-4">
        <div>
          <h3 className="text-sm font-medium text-slate-600">Description</h3>
          <p className="text-sm text-slate-700 mt-1">{task.description || 'No description'}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <h3 className="text-sm font-medium text-slate-600">Category</h3>
            <p className="text-sm text-slate-700 capitalize">{task.category || 'General'}</p>
            {task.floorNumber && (
              <p className="text-sm text-slate-700">Floor: {task.floorNumber}</p>
            )}
          </div>
          <div>
            <h3 className="text-sm font-medium text-slate-600">Assigned To</h3>
            <p className="text-sm text-slate-700">
              {assignedTo ? assignedTo.name : 'Not assigned yet'}
            </p>
          </div>
        </div>

        {/* Client Info - role-based */}
        {client && (
          <div className="bg-slate-50 rounded-lg p-4">
            <h3 className="text-sm font-medium text-slate-600 mb-2">Client Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <div className="flex items-center gap-2 text-sm">
                <User className="w-4 h-4 text-slate-400" />
                {client.name}
              </div>
              {!isFreelancer || isAdmin ? (
                <>
                  <div className="flex items-center gap-2 text-sm">
                    <Mail className="w-4 h-4 text-slate-400" />
                    {client.email || 'N/A'}
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Phone className="w-4 h-4 text-slate-400" />
                    {client.phone || 'N/A'}
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <MapPin className="w-4 h-4 text-slate-400" />
                    {client.address || 'N/A'}
                  </div>
                </>
              ) : (
                <>
                  <div className="flex items-center gap-2 text-sm">
                    <MapPin className="w-4 h-4 text-slate-400" />
                    Pincode: {client.pincode || 'N/A'}
                  </div>
                  <div className="text-xs text-slate-400 italic col-span-2">
                    (Contact details hidden for freelancers)
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Submissions */}
        {submissions.length > 0 && (
          <div>
            <h3 className="text-sm font-medium text-slate-600 mb-2">Submissions</h3>
            <div className="space-y-2">
              {submissions.map((sub) => (
                <div key={sub.id} className="flex items-center gap-3 text-sm bg-slate-50 p-2 rounded-lg">
                  <FileText className="w-4 h-4 text-slate-400" />
                  <span>Version {sub.version || 1}</span>
                  <span className="text-slate-400">•</span>
                  <span>{formatDate(sub.created)}</span>
                  <button
                    onClick={() => handleDownload(sub)}
                    className="ml-auto text-indigo-600 hover:text-indigo-800 transition"
                  >
                    <Download className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        {canClaim && (
          <button
            onClick={handleClaim}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition flex items-center gap-2"
          >
            <Check className="w-4 h-4" /> Claim Task
          </button>
        )}
        {canStart && (
          <button
            onClick={handleStart}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
          >
            Start Work
          </button>
        )}
        {canSubmit && (
          <button
            onClick={() => setShowSubmitModal(true)}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition flex items-center gap-2"
          >
            <Upload className="w-4 h-4" /> Submit Design
          </button>
        )}
        {canComplete && (
          <button
            onClick={handleComplete}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
          >
            Complete Task
          </button>
        )}
        <button
          onClick={() => navigate('/designer/tasks')}
          className="px-4 py-2 border border-slate-300 text-slate-600 rounded-lg hover:bg-slate-50 transition"
        >
          Back to Tasks
        </button>
      </div>

      {/* Submit Modal */}
      {showSubmitModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-slate-800">Submit Design</h3>
              <button
                onClick={() => {
                  setShowSubmitModal(false);
                  setSubmitFile(null);
                  setSubmitNote('');
                }}
                className="text-slate-400 hover:text-slate-600 transition"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm text-slate-500 mb-4">
              Task: <span className="font-medium text-slate-700">{task.title}</span>
            </p>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">File *</label>
                <input
                  type="file"
                  onChange={(e) => setSubmitFile(e.target.files[0])}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500/20 outline-none"
                  required
                  accept=".pdf,.dwg,.jpg,.jpeg,.png,.zip"
                />
                <p className="text-xs text-slate-400 mt-1">Accepted: PDF, DWG, JPG, PNG, ZIP</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Note (optional)</label>
                <textarea
                  value={submitNote}
                  onChange={(e) => setSubmitNote(e.target.value)}
                  rows="3"
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500/20 outline-none"
                  placeholder="Any comments about this design..."
                />
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowSubmitModal(false);
                    setSubmitFile(null);
                    setSubmitNote('');
                  }}
                  className="px-4 py-2 text-sm text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50 transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition flex items-center gap-2 disabled:opacity-50"
                >
                  <Send className="w-4 h-4" /> {submitting ? 'Submitting...' : 'Submit'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default DesignerTaskDetail;